import type { ToggleOption } from "@replo/design-system/components/toggle/ToggleGroup";
import type { DirectionModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSelectedElement } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface DirectionModifierProps {
  onChange: (modifiers: DirectionModifierValue[]) => void;
  disabled?: boolean;
}

const DirectionModifierComponent: React.FC<DirectionModifierProps> = ({
  onChange,
  disabled = false,
}) => {
  const selectedElement = useSelectedElement();

  const flexDirection = selectedElement?.styleOverrides.flexDirection;
  // NOTE (Kurt, 2025-07-25): We only support row and column directions for now. We do this check since
  // the LLM can generate other directions that we don't support yet.
  const currentValue =
    flexDirection === "row" || flexDirection === "column"
      ? flexDirection
      : null;

  const [direction, setDirection] = useOverridableState<string>(
    currentValue ?? "",
    (newDirection: string) => {
      onChange([
        {
          type: "containerDirection" as const,
          value: newDirection as DirectionModifierValue["value"],
        },
      ]);
    },
  );

  const directionOptions: ToggleOption[] = [
    {
      value: "row",
      label: <ArrowRight size={16} />,
      tooltipContent: "Horizontal",
    },
    {
      value: "column",
      label: <ArrowDown size={16} />,
      tooltipContent: "Vertical",
    },
  ];

  return (
    <div className="container-direction-modifier">
      <ToggleGroup
        size="sm"
        selectedValue={direction}
        options={directionOptions}
        onChange={setDirection}
        disabled={disabled}
        layoutClassName="w-full"
      />
    </div>
  );
};

export default DirectionModifierComponent;
